import type { ResumeRoleBullet } from '@careeropt/schema';
import { resolveResumeBullets } from '../../lib/resumeBulletResolve';
import { RESUME_CSS } from './ResumeView';
import type { AchievementRecord, RoleRecord } from '../../lib/api';

// =============================================================================
// RoleResumeSection (throughline) — ONE era, rendered AS an expanded résumé
// fragment. Ported 1:1 from apps/web/src/components/resume/RoleResumeSection.tsx
// (Jason, direct: match the existing format exactly — this is a re-skin, not a
// redesign). Structure, fallback rule, and doc comments below mirror that file;
// the only change is routing color through throughline's --tl-* tokens via the
// re-skinned RESUME_CSS this app's own ResumeView.tsx exports, and dropping the
// rewrite-affordance button (onReframe/ReframeSheet is an apps/web-only,
// Wand2-triggered flow this app has no equivalent surface for yet — omitting an
// action neither app brief asked for is not a content change).
//
// This IS the "per-era expand that reads like an expanded CV" the brief names
// as apps/web's ground truth: role-header (title, company, dates, descriptor)
// + ordered bullets, the same .rd-* markup ResumeView uses, so an expanded era
// on Refine looks byte-for-byte like a fragment of the printed résumé.
//
// THIN PRESENTATION / NO INVENTION: every line is a 1:1 map of a served value.
// When the parent has the composed corpus-résumé document for this era (the
// `servedBullets` prop — the SERVED, gated, ghostwritten synthesis from
// GET /api/corpus/resume), each line renders that document's `bullet.text` —
// the same string ResumeView prints — keyed to its real `achievementId`. When
// the document is not loaded yet, or has no bullet for a given card, the line
// falls back to that card's verbatim `headline` (exactly the backend
// composer's own ghostwrite fallback: an absent override = the verbatim
// headline). Nothing is composed, ordered-by-model, or fabricated here. The
// resolution rule itself is extracted to lib/resumeBulletResolve.ts (pure,
// unit-pinned) rather than inlined, so the served-wins/fallback decision has
// its own test coverage independent of this component's render.
//
// PROVENANCE HIDDEN BY CONSTRUCTION: unlike AchievementCard (which renders
// `achievement.tags` verbatim as chips — the exact leak issue #44 documents:
// internal carriers like `pkg:enabler-record-v1` / `claim:receipted-fact` /
// `src:ET-L61-64` riding in that array), this component never reads `.tags` at
// all. The résumé view only ever surfaces `text`/`kpi` (ghostwritten bullet or
// verbatim headline + its own kpiValue) — there is no code path here through
// which a tag string could reach the DOM.
// =============================================================================

function fmtDate(d?: string | null): string {
  if (!d) return '';
  const m = /^(\d{4})-(\d{2})/.exec(d);
  if (!m) {
    // Corpus dates can be full ISO; fall back to a stable Mon YYYY.
    const dt = new Date(d);
    return Number.isNaN(dt.getTime())
      ? d
      : dt.toLocaleDateString(undefined, { month: 'short', year: 'numeric' });
  }
  const months = [
    'Jan',
    'Feb',
    'Mar',
    'Apr',
    'May',
    'Jun',
    'Jul',
    'Aug',
    'Sep',
    'Oct',
    'Nov',
    'Dec',
  ];
  return `${months[Number(m[2]) - 1] ?? ''} ${m[1]}`.trim();
}

function roleDates(start: string, end: string | null, isCurrent: boolean): string {
  const s = fmtDate(start);
  const e = isCurrent ? 'Present' : fmtDate(end);
  return [s, e].filter(Boolean).join(' – ');
}

export function RoleResumeSection({
  role,
  items,
  servedBullets,
  includeStyle = true,
}: {
  /** The era. null for the orphan "Unattached" bucket — header is generic. */
  role: RoleRecord | null;
  /** The era's active cards, already ordered by the parent. */
  items: AchievementRecord[];
  /** The SERVED ghostwritten bullets for THIS era, sliced from the corpus-résumé
   *  document by the parent (the matching `experience` role's `bullets`). When
   *  present, each card renders its document `bullet.text` (the gated synthesis)
   *  instead of the raw headline. Undefined/empty (document not loaded, or no
   *  matching role) → every line falls back to the card's verbatim headline. */
  servedBullets?: ResumeRoleBullet[];
  /** Inline the shared résumé CSS. True (default) for a standalone fragment;
   *  pass false when a sibling already mounted ResumeView's <style> on the page
   *  so the rule block isn't duplicated. */
  includeStyle?: boolean;
}) {
  // Filter-active + serve-or-fallback resolution both live in the extracted,
  // unit-pinned resolveResumeBullets (lib/resumeBulletResolve.ts) — NO
  // INVENTION: both its branches are real served values, never composed here.
  const bullets = resolveResumeBullets(items, servedBullets);

  return (
    <div className="resume-doc resume-doc--fragment">
      {includeStyle ? <style>{RESUME_CSS}</style> : null}
      <style>{FRAGMENT_CSS}</style>

      <div className="rd-role">
        <div className="rd-role-head">
          <span>
            <span className="rd-role-title">{role ? role.title : 'Unattached achievements'}</span>
            {role ? (
              <>
                {', '}
                <span className="rd-role-co">{role.company}</span>
                {role.location ? <span className="rd-muted">{`  ${role.location}`}</span> : null}
              </>
            ) : null}
          </span>
          {role ? (
            <span className="rd-role-dates">
              {roleDates(role.startDate, role.endDate, role.isCurrent === 1)}
            </span>
          ) : null}
        </div>
        {role?.employerDescriptor ? (
          <div className="rd-role-desc">{role.employerDescriptor}</div>
        ) : null}

        {bullets.length ? (
          <ul className="rd-bullets">
            {bullets.map((b) => (
              <li className="rd-bullet" key={b.achievementId}>
                {b.text}
                {b.kpi ? <span className="rd-kpi">{`  (${b.kpi})`}</span> : null}
              </li>
            ))}
          </ul>
        ) : (
          <p className="rd-muted" style={{ margin: '4px 0 0', fontStyle: 'italic' }}>
            No active cards in this era yet.
          </p>
        )}
      </div>
    </div>
  );
}

// Fragment-only tweak layered on top of RESUME_CSS: drop the full A4 page chrome
// (the fragment lives inside a card, not a printed page) — never touches
// ResumeView's own render. Mirrors apps/web's identical FRAGMENT_CSS override.
const FRAGMENT_CSS = `
.resume-doc--fragment {
  width: auto; min-height: 0; padding: 0; margin: 0; background: transparent;
}
`;
